import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '#/components/ui/dialog';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import type { OfferPlatformV2 } from '~/api/v2/schemas/offer-platform';
import { DATA_AREAS } from '~/constants/data-areas';
import { SearchHistory } from '~/features/search-history';
import { useSearchHistoryTracker } from '~/features/search-history/use-search-history-tracker';

import { searchBoxStyles, searchBoxTriggerStyles, searchHistoryStyles } from '../styles';
import { SvgIconClose } from '../svg/icon-close';
import SiteSearchBox from './site-search-box';
import SiteSearchBoxTrigger from './site-search-box-trigger';

export const SiteModalSearchKeyword = ({ path, offerPlatform }: { path: string; offerPlatform: OfferPlatformV2[] }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const { addKeyword, keywords } = useSearchHistoryTracker();

  const getParams = (keyword: string) => {
    const params = new URLSearchParams({ keyword });
    if (path === '/') {
      params.append('campaign_type', '1');
      params.append('reward_type', '1');
    } else if (path === `${import.meta.env.VITE_SERVICE_TOP_ROUTE}`) {
      params.append('campaign_type', '1');
      params.append('reward_type', '2');
    } else if (path === `${import.meta.env.VITE_APPLICATION_TOP_ROUTE}`) {
      params.append('campaign_type', '2');
      params.append('reward_type', '2');
      params.append('offer_platform', offerPlatform.join(','));
    }
    return params;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (keyword.trim()) {
      addKeyword(keyword.trim());
    }
    const params = getParams(keyword);
    navigate(`${import.meta.env.VITE_CAMPAIGN_SEARCH_ROUTE}?${params.toString()}`);
  };

  return (
    <div data-area={DATA_AREAS.SITE_MODAL_SEARCH_KEYWORD}>
      <Dialog
        onOpenChange={(open) => {
          if (open) {
            setTimeout(() => {
              inputRef.current?.focus();
            }, 0);
          }
        }}>
        <DialogTrigger asChild>
          <SiteSearchBoxTrigger styles={searchBoxTriggerStyles} placeholderText="サービス名やショップ名を検索" />
        </DialogTrigger>

        <DialogContent
          classNameOverlay={'hidden'}
          className="h-dvh w-full max-w-full grid-rows-[50px_1fr] gap-0 rounded-none bg-white px-4 pt-0 pb-20 data-[state=closed]:animate-none data-[state=open]:animate-none">
          <DialogHeader className="flex justify-end">
            <DialogTitle className="sr-only">検索</DialogTitle>
            <DialogDescription className="sr-only">なにをお探しですか？</DialogDescription>
            <DialogClose asChild>
              <button type="button" aria-label="Close">
                <SvgIconClose width={24} height={24} className="text-grey-900 size-6 flex-none" />
              </button>
            </DialogClose>
          </DialogHeader>

          <div className="main-container mt-18 flex flex-col gap-6">
            <form onSubmit={handleSubmit}>
              <SiteSearchBox
                inputRef={inputRef}
                styles={searchBoxStyles}
                name="keyword"
                placeholderText="サービス名やショップ名を検索"
                keyword={keyword}
                onKeywordChange={setKeyword}
              />
            </form>
            {keywords.length > 0 && (
              <div className="-my-2 flex w-full flex-col space-y-6 overflow-hidden px-4 py-2 pr-2 md:mx-auto md:max-w-[742px]">
                <div className="flex w-full flex-col gap-2 place-self-center overflow-hidden">
                  <p className="text-caption-m text-grey-500">最近の検索</p>
                  <SearchHistory
                    styles={searchHistoryStyles}
                    closeButton={<SvgIconClose width={16} height={16} />}
                    getParams={getParams}
                  />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
